import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Logo } from './logo'
import {
  CheckCircle2,
  ArrowRight,
  LayoutDashboard,
  Bell,
  BarChart3,
  AlertTriangle,
  Link,
} from 'lucide-react'
import confetti from 'canvas-confetti'
import { useEffect } from 'react'

interface OnboardingSuccessProps {
  locationCount: number
  onGoToDashboard: () => void
  dataSource?: 'google' | 'savoria'
}

export function OnboardingSuccess({
  locationCount,
  onGoToDashboard,
  dataSource = 'google',
}: OnboardingSuccessProps) {
  const { t } = useTranslation()

  useEffect(() => {
    const duration = 2000
    const end = Date.now() + duration

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors: ['#E8694D', '#8BA98E', '#A8C686'],
      })
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors: ['#E8694D', '#8BA98E', '#A8C686'],
      })

      if (Date.now() < end) {
        requestAnimationFrame(frame)
      }
    }

    frame()
  }, [])

  const googleNextSteps = [
    {
      icon: LayoutDashboard,
      title: t('onboarding.successGoogleStep1Title'),
      description: t('onboarding.successGoogleStep1Desc'),
    },
    {
      icon: Bell,
      title: t('onboarding.successGoogleStep2Title'),
      description: t('onboarding.successGoogleStep2Desc'),
    },
    {
      icon: BarChart3,
      title: t('onboarding.successGoogleStep3Title'),
      description: t('onboarding.successGoogleStep3Desc'),
    },
  ]

  const savoriaNextSteps = [
    {
      icon: LayoutDashboard,
      title: t('onboarding.successSavoriaStep1Title'),
      description: t('onboarding.successSavoriaStep1Desc'),
    },
    {
      icon: Link,
      title: t('onboarding.successSavoriaStep2Title'),
      description: t('onboarding.successSavoriaStep2Desc'),
    },
    {
      icon: BarChart3,
      title: t('onboarding.successSavoriaStep3Title'),
      description: t('onboarding.successSavoriaStep3Desc'),
    },
  ]

  const nextSteps = dataSource === 'google' ? googleNextSteps : savoriaNextSteps

  return (
    <div className="min-h-screen bg-warm-gray flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Logo />
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center space-y-8">
          {/* Success Icon */}
          <div className="relative">
            <div className="w-24 h-24 rounded-full bg-accent/10 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-12 h-12 text-accent" />
            </div>
            <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-primary flex items-center justify-center text-white text-sm font-bold animate-bounce">
              {locationCount}
            </div>
          </div>

          {/* Message */}
          <div className="space-y-2">
            <h1 className="text-3xl font-bold text-foreground">{t('onboarding.successTitle')}</h1>
            <p className="text-muted-foreground">
              {t('onboarding.successLocationsConnected', { count: locationCount })}{' '}
              {dataSource === 'google'
                ? t('onboarding.successGatheringGoogle')
                : t('onboarding.successGatheringSavoria')}
            </p>
          </div>

          {dataSource === 'savoria' && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 text-left">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">
                  {t('onboarding.successLimitedTitle')}
                </p>
                <p className="text-sm text-amber-700 mt-1">{t('onboarding.successLimitedDesc')}</p>
              </div>
            </div>
          )}

          {/* Next Steps */}
          <div className="bg-white rounded-2xl border border-border p-6 text-left space-y-4">
            <h2 className="font-semibold text-foreground">{t('onboarding.whatsNext')}</h2>
            <div className="space-y-3">
              {nextSteps.map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center flex-shrink-0">
                    <step.icon className="w-4 h-4 text-muted-foreground" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-foreground">{step.title}</h3>
                    <p className="text-sm text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <Button
            onClick={onGoToDashboard}
            size="lg"
            className="w-full h-14 text-base font-semibold gap-2 group"
          >
            {t('onboarding.goToDashboard')}
            <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
          </Button>
        </div>
      </main>
    </div>
  )
}
